import React from 'react';
import { Form } from 'react-bootstrap';
import { Select } from 'antd';
const { Option } = Select;

// The one multi-select control for the diagnosis form.
//
// The form used to mix two libraries: every single-select was antd, while all
// four multi-selects (Current Diagnosis, Limb Loss, Mutation, Previous Cancer
// History) were multiselect-react-dropdown. Current Diagnosis sits in the same
// row as Primary Site, so adding a fifth multi-select made the split obvious.
// All five now render through here.
//
// THE CONTRACT: `value` and `onChange` deal in ARRAYS OF OPTION OBJECTS, not
// ids — exactly what multiselect-react-dropdown handed back. That is deliberate
// and is what made the migration a JSX-only change: the callers' handlers,
// hydration, validation and payload builders (which read `obj.id`) all kept
// working untouched. antd talks in ids; the object<->id mapping happens in here.
//
// Selections are matched by key, and the objects the caller passed in are handed
// straight back, so anything riding along on them (diagnosisPrimarySiteId,
// leadSite, tumorGroup) survives a round trip.
export default function MultiSelectField(props) {
  const {
    label,
    required,
    error,
    options,
    value,
    onChange,
    idKey = 'id',
    labelKey = 'name',
    // When set, options render as two lines (name + grey sub-line) and the
    // search matches the description too. Only Primary Site uses it.
    descriptionKey,
    placeholder = '- Select all that apply -',
    loading,
    disabled,
    maxTagCount,
    groupClassName,
    className,
    // Not optional in practice: antd portals the dropdown panel to
    // document.body, outside .diagnosis-container, so this class is the only
    // hook DiagnosisManagement.css has on it. See that file's header.
    dropdownClassName = 'dx-dropdown',
    listHeight,
    notFoundText = 'No matching option',
  } = props;

  const available = Array.isArray(options) ? options : [];
  const selected = Array.isArray(value) ? value : [];

  const labelOf = (item) => String((item && item[labelKey]) || '');

  // A real id when there is one, otherwise a name-derived token. Free-text
  // entries (siteId: null) only ever arrive from the backend, never from this
  // control, but they still need a stable key to render and round-trip.
  // Everything is stringified so antd sees one value type.
  const keyOf = (item) => {
    const raw = item ? item[idKey] : null;
    const isNothing =
      raw === null || raw === undefined || raw === '' || Number(raw) === 0 || Number(raw) === -1;
    return isNothing ? 'name:' + labelOf(item).trim().toLowerCase() : String(raw);
  };

  const selectedKeys = selected.map(keyOf);

  // Selected objects take precedence over the option list: they carry fields
  // the master rows do not, and for a retired master row they are the only
  // place the name exists at all.
  const byKey = new Map();
  available.forEach((item) => byKey.set(keyOf(item), item));
  selected.forEach((item) => byKey.set(keyOf(item), item));

  const handleChange = (keys) => {
    if (!onChange) return;
    onChange((keys || []).map((key) => byKey.get(key)).filter(Boolean));
  };

  const renderOption = (item) => {
    const text = labelOf(item);
    const description = descriptionKey ? item[descriptionKey] : '';

    return (
      <Option
        key={keyOf(item)}
        value={keyOf(item)}
        label={text}
        optiondescription={description || ''}
      >
        {description && description !== text ? (
          <div style={{ display: 'flex', flexDirection: 'column', padding: '2px 0', lineHeight: 1.3 }}>
            <span style={{ fontWeight: 500, color: '#262626' }}>{text}</span>
            <span
              className="primary-site-desc"
              style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '2px' }}
            >
              {description}
            </span>
          </div>
        ) : (
          text
        )}
      </Option>
    );
  };

  // A selection whose master row is no longer in the option list — retired, or
  // simply not loaded yet. Every caller hydrates its selection from the saved
  // record rather than resolving against the options, so without these antd
  // would render a bare key where a real value exists, and the next save would
  // drop it. multiselect-react-dropdown never had this problem because it
  // rendered selectedValues directly.
  const availableKeys = new Set(available.map(keyOf));
  const ghosts = selected.filter((item) => !availableKeys.has(keyOf(item)));

  return (
    <Form.Group className={groupClassName}>
      {label && (
        <Form.Label>
          {label}
          {required && <span className="text-red"> *</span>}
        </Form.Label>
      )}
      <Select
        mode="multiple"
        className={`w-100 ${error ? 'is-invalid' : ''} ${className || ''}`.trim()}
        dropdownClassName={dropdownClassName}
        placeholder={placeholder}
        value={selectedKeys}
        onChange={handleChange}
        loading={loading}
        disabled={disabled}
        maxTagCount={maxTagCount}
        listHeight={listHeight}
        showSearch
        optionLabelProp="label"
        filterOption={(input, option) => {
          if (!input) return true;
          const q = input.toLowerCase();
          return (
            String(option.label || '').toLowerCase().includes(q) ||
            String(option.optiondescription || '').toLowerCase().includes(q)
          );
        }}
        notFoundContent={
          <div style={{ padding: '10px', textAlign: 'center', color: '#999' }}>{notFoundText}</div>
        }
      >
        {available.map(renderOption)}
        {ghosts.map(renderOption)}
      </Select>
      {error && <div className="invalid-feedback d-block">{error}</div>}
    </Form.Group>
  );
}
